import React, {useEffect, useMemo, useState} from 'react';
import Input from '../../Input';

import Checkbox from '../../Checkbox';
import {useDebounce} from '../../../pages/LogSheets/hooks';
import {toast} from 'react-toastify';
import {useDispatch} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {useTranslation} from '../../../hooks/useTranslation';

const CustomInspectionsBuildModal = ({
  exit,
  items,
  selectedItems,
  setSelectedItems,
  title,
  itemTitle,
  actionTitle,
  newTitle,
  createAction = () => {},
  type,
  ifShowCreate = true,
  equipmentTypeCategory,
  getEquipmentsActions,
  // Generic pagination props
  isFetching,
  loaderRef,
  setPage,
  maxPage,
  currentPage,
  searchValue = '',
  onSearchChange,
}) => {
  const {t} = useTranslation();
  const [search, setSearch] = useState(searchValue);
  const debouncedSearchValue = useDebounce(search, 500);
  const [itemsList, setItemsList] = useState(items || []);
  const [chosenItems, setChosenItems] = useState(selectedItems || []);
  let field = type === 'equipment' ? 'title' : 'name';

  // Reset search when modal opens
  useEffect(() => {
    if (searchValue === '' && search !== '') {
      setSearch('');
    }
  }, [searchValue]);

  useEffect(() => {
    setChosenItems(selectedItems);
    let changedArray = items?.map(item => {
      selectedItems?.map(selectedItem => {
        if (selectedItem?.id === item?.id) {
          item = {...item, checked: true};
        }
      });
      return item;
    });
    setItemsList(changedArray);
  }, [selectedItems, items]);

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearchValue);
    }
  }, [debouncedSearchValue, onSearchChange]);

  useEffect(() => {
    // Only set up infinite scroll if pagination props are provided
    if (!loaderRef || !setPage || !maxPage || !currentPage) return;

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];
        if (target.isIntersecting && !isFetching) {
          if (maxPage !== currentPage) {
            setPage(prevPage => prevPage + 1);
          }
        }
      },
      {threshold: 1.0},
    );

    if (loaderRef?.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [isFetching, maxPage, currentPage, loaderRef, setPage]);

  const selectItem = ({checked, item}) => {
    if (checked) {
      setChosenItems(prev => [...prev, {...item, checked: checked}]);
    } else {
      setChosenItems(chosenItems?.filter(form => form.id !== item.id));
    }
    setItemsList(old =>
      old?.map(el => (el.id === item.id ? {...el, checked: checked} : el)),
    );
  };

  const clearAllItems = () => {
    setChosenItems([]);
    setItemsList(old => old?.map(el => ({...el, checked: false})));
  };

  const renderItemsList = useMemo(() => {
    if (itemsList?.length > 0) {
      return itemsList?.map(item => (
        <div className='modal__regions-elem' key={item?.id}>
          <Checkbox
            checked={item?.checked}
            onChange={e => selectItem({checked: e.target.checked, item})}
          />
          <p>{item[field]}</p>
        </div>
      ));
    } else {
      return (
        <div className='modal__regions-empty'>
          <p>{t('customInspectionsBuildModal.noItemsFound') + ' ' + itemTitle}</p>
        </div>
      );
    }
  }, [itemsList]);

  const addItems = () => {
    setSelectedItems(chosenItems);
    exit();
  };

  return (
    <div className='modal modal-fbo regions'>
      <div className='modal__regions'>
        <div className='modal__regions-header'>
          <p className='modal__regions-title'>{title}</p>
          <img
            className='modal__content-exit'
            src='/assets/icons/close.svg'
            alt='close'
            onClick={exit}
          />
        </div>
        <div className='modal__regions-body modify-items'>
          {chosenItems?.length ? (
            <div style={{width: '100%'}}>
              <div className='modal__regions-items-header'>
                <p className='modal__regions-inputSubTitle'>
                  {t('customInspectionsBuildModal.selectedItems', {itemTitle})}
                </p>
                <p
                  className='modal__regions-inputSubTitle red'
                  onClick={clearAllItems}
                >
                  {t('customInspectionsBuildModal.clearAll')}
                </p>
              </div>
              <div className='modal__regions-list selected-items'>
                {chosenItems?.map(item => (
                  <div className='modal__regions-elem' key={item?.id}>
                    <Checkbox
                      checked={item?.checked}
                      onChange={e =>
                        selectItem({checked: e.target.checked, item: item})
                      }
                    />
                    <p>{item[field]}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <Input
            name='regionSearch'
            id='regionSearch'
            image={'/assets/icons/search.svg'}
            maxwidth={600}
            placeholder={t('customInspectionsBuildModal.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div className='modal__regions-list all-items'>
            {renderItemsList}
            {loaderRef && setPage && (
              <div
                ref={loaderRef}
                style={{
                  height: 40,
                  backgroundColor: 'transparent',
                  width: '100%',
                }}
              >
                {isFetching ? t('customInspectionsBuildModal.loading') : ''}
              </div>
            )}
          </div>
        </div>
        <div className='modal__regions-bottom item-bottom'>
          {type === 'equipment' ? (
            !ifShowCreate ? (
              <div
                className='modal__regions-btn create-item'
                onClick={createAction}
              >
                {t('customInspectionsBuildModal.createNew') + ' ' + newTitle}
              </div>
            ) : (
              <div className='modal__regions-warning'>
                <img src='/assets/icons/warning.svg' alt='info' />
                <p>
                  {t('customInspectionsBuildModal.warningMessage')}
                </p>
              </div>
            )
          ) : (
            ifShowCreate && (
              <div
                className='modal__regions-btn create-item'
                onClick={createAction}
              >
                {t('customInspectionsBuildModal.create') + ' ' + newTitle}
              </div>
            )
          )}
          <div className='modal__regions-bottom-additional-buttons'>
            <div className='modal__regions-btn cancel' onClick={exit}>
              {t('customInspectionsBuildModal.cancel')}
            </div>
            <div
              className={`modal__regions-btn ${chosenItems?.length === 0 ? 'disabled' : ''}`}
              onClick={() => {
                if (chosenItems?.length === 0) {
                  return;
                } else {
                  addItems();
                }
              }}
            >
              {t('customInspectionsBuildModal.addSelected') + ' ' + actionTitle}
            </div>
          </div>
        </div>
      </div>
      <div className='modal__bg'></div>
    </div>
  );
};

export default CustomInspectionsBuildModal;
